import { useEffect, useRef } from "react";
import getConfig from "next/config";
import Md5 from "md5";
import { Swiper, SwiperSlide } from "swiper/react";
import { Keyboard, Navigation } from "swiper/modules";

import { formatDate } from "@lib/helpers";
import VerifiedBuyIcon from "@components/Icons/VerifiedBuy";
import ReviewProductVariation from "@containers/Reviews/ReviewProductVariation";
import StarLabel from "@components/StarLabel";
import CarouselNavArrow from "@components/Icons/CarouselNavArrow";

import "swiper/css";
import "swiper/css/navigation";

const {
  publicRuntimeConfig: { settings },
} = getConfig();

const ReviewDetails = ({
  reviewDetails,
  productVariations,
  onPrevImageChange,
  onNextImageChange,
  currentImageIndex,
}) => {
  const {
    _id: reviewId,
    customer,
    date,
    verifiedBuyer,
    productSku,
    rating,
    title,
    body,
    images,
  } = reviewDetails;

  const swiperRef = useRef(null);

  // set active index
  useEffect(() => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.activeIndex = currentImageIndex;

      // Update swiper after changing active index
      swiperRef.current.swiper.update();
    }
  }, [reviewDetails]);

  return (
    <>
      <div className="flex items-center mb-[25px] sm:mb-0 w-full sm:w-[50%] relative self-baseline">
        <div
          onClick={onPrevImageChange}
          className="arrow-left absolute w-[30px] z-10 left-[10px] rounded-full bg-gray-1300 cursor-pointer"
        >
          <CarouselNavArrow />
        </div>

        <Swiper
          ref={swiperRef}
          className="overflow-hidden"
          centeredSlides={true}
          modules={[Navigation, Keyboard]}
          navigation={{ nextEl: ".arrow-right", prevEl: ".arrow-left" }}
          initialSlide={currentImageIndex}
          slidesPerGroup={1}
          slidesPerView={1}
          watchOverflow={false}
          keyboard={true}
        >
          {images.map((image) => (
            <SwiperSlide key={image}>
              <img
                className="object-cover"
                src={`${settings.cdn}/resize/reviews-files/${reviewId}/${Md5(
                  "600x600"
                )}/${image}`}
                alt={reviewId}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        <div
          onClick={onNextImageChange}
          className="arrow-right absolute w-[30px] z-10 right-[10px] rounded-full bg-gray-1300 cursor-pointer"
        >
          <CarouselNavArrow direction="right" />
        </div>
      </div>

      <div className="sm:w-[50%] sm:pl-[15px]">
        {customer?.alias && (
          <div className="font-bold leading-none">{customer.alias}</div>
        )}

        {date && (
          <div className="text-[#808080] text-[14px] mb-[16px]">
            {formatDate(date)}
          </div>
        )}

        {verifiedBuyer && (
          <div className="flex items-center mb-[14px]">
            <VerifiedBuyIcon />

            <div className="leading-none ml-[4px]">Achizitie verificata</div>
          </div>
        )}

        {productVariations && (
          <div className="inline-block w-full mb-[16px]">
            <ReviewProductVariation variation={productVariations[productSku]} />
          </div>
        )}

        {rating && (
          <div className="mb-[14px]">
            <StarLabel value={rating} />
          </div>
        )}

        {title && (
          <div className="text-[18px] font-medium mb-[8px]">{title}</div>
        )}

        {body && <p className="text-[14px]">{body}</p>}
      </div>
    </>
  );
};

export default ReviewDetails;
